﻿import React, { useState, useEffect, useRef } from 'react'
import { XMarkIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { Button, Text } from './ui'
import { useAuthContext } from '../contexts/AuthContext'
import { notificationsService } from '../services/notifications'
import { userGroupsService } from '../services/modules'
import { UserGroup } from '../types/modules'

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const { user, profile } = useAuthContext()
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [adminGroupId, setAdminGroupId] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)

  // Buscar grupo de administradores ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      const fetchAdminGroup = async () => {
        try {
          const result = await userGroupsService.getAll()
          if (result.success && result.data) {
            // Buscar grupo admin (is_admin = true ou name = 'admin')
            const adminGroup = result.data.find(
              (group: UserGroup) => group.is_admin === true || group.name === 'admin'
            )
            if (adminGroup) {
              setAdminGroupId(adminGroup.id)
            } else {
              setError('Grupo de administradores não encontrado')
            }
          }
        } catch (err) {
          console.error('Erro ao buscar grupo admin:', err)
          setError('Erro ao buscar grupo de administradores')
        }
      }
      fetchAdminGroup()
      
      previouslyFocusedElementRef.current = (document.activeElement as HTMLElement) || null
      setTimeout(() => {
        const textarea = dialogRef.current?.querySelector<HTMLElement>('textarea')
        textarea?.focus()
      }, 0)
    }
  }, [isOpen])

  // Resetar estados ao fechar
  useEffect(() => {
    if (!isOpen) {
      setMessage('')
      setError(null)
      setSuccess(null)
    }
  }, [isOpen])

  // Bloquear scroll do body quando modal estiver aberto
  useEffect(() => {
    if (!isOpen) return
    const body = document.body
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = previousOverflow
    }
  }, [isOpen])

  // Gerenciar teclas (Escape e Tab)
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!loading) {
          handleClose()
        }
      }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
        )
        if (!focusable || focusable.length === 0) return
        const focusableArray = Array.from(focusable)
        const first = focusableArray[0]
        const last = focusableArray[focusableArray.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, loading])

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !loading) {
      handleClose()
    }
  }

  const handleClose = () => {
    previouslyFocusedElementRef.current?.focus()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!message.trim()) {
      setError('Por favor, descreva sua sugestão ou necessidade de ajuda')
      return
    }

    if (!adminGroupId) {
      setError('Grupo de administradores não encontrado. Tente novamente mais tarde.')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      // Criar título da notificação
      const userName = profile?.name || user?.email || 'Usuário'
      const title = `Sugestão ou Pedido de Ajuda - ${userName}`

      // Criar corpo da notificação com informações do usuário
      const userInfo = `Usuário: ${userName}`
      const userEmail = user?.email ? `\nE-mail: ${user?.email}` : ''
      const helpMessage = `\n\nMensagem:\n${message.trim()}`
      const body = `${userInfo}${userEmail}${helpMessage}`

      // Enviar notificação para o grupo admin
      const result = await notificationsService.sendToGroups({
        title,
        body,
        userGroupIds: [adminGroupId],
        userIds: []
      })

      if (result.error) {
        throw result.error
      }

      setSuccess('Sua sugestão ou pedido de ajuda foi enviado com sucesso! Os administradores serão notificados.')
      setMessage('')
      
      // Fechar modal após 2 segundos
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err) {
      console.error('Erro ao enviar pedido de ajuda:', err)
      setError((err as Error).message || 'Erro ao enviar sugestão ou pedido de ajuda. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" 
      onMouseDown={handleOverlayMouseDown}
    >
      <div 
        ref={dialogRef} 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="help-modal-title" 
        aria-describedby="help-modal-description" 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-purple-600 rounded-full overflow-hidden flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 id="help-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Sugestões ou Precisa de ajuda
              </h2>
              <p id="help-modal-description" className="text-sm text-gray-500 dark:text-gray-400">
                Envie sua sugestão ou descreva sua necessidade. Os administradores serão notificados
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Fechar modal de ajuda"
            disabled={loading}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Mensagem */}
          <div>
            <label 
              htmlFor="help-message" 
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Sua sugestão ou necessidade de ajuda
            </label>
            <div className="relative">
              <textarea
                id="help-message"
                value={message}
                onChange={(e) => {
                  const newValue = e.target.value.slice(0, 500)
                  setMessage(newValue)
                  setError(null)
                }}
                className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                placeholder="Descreva sua sugestão ou necessidade de ajuda de forma detalhada..."
                required
                maxLength={500}
                disabled={loading}
              />
              <div className="mt-1 flex justify-between items-center">
                <Text size="xs" color="muted">
                  Seja específico sobre sua sugestão, problema ou dúvida
                </Text>
                <Text 
                  size="xs" 
                  className={message.length > 450 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}
                >
                  {message.length}/500
                </Text>
              </div>
            </div>
          </div>

          {/* Mensagens de erro/sucesso */}
          {error && (
            <div 
              role="alert" 
              className="p-3 rounded-lg text-sm bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            >
              {error}
            </div>
          )}

          {success && (
            <div 
              role="status" 
              aria-live="polite"
              className="p-3 rounded-lg text-sm bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
            >
              {success}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={loading || !message.trim() || !adminGroupId}
              aria-busy={loading}
            >
              {loading ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default HelpModal
